import { Injectable, Inject, Logger, HttpException, HttpStatus } from '@nestjs/common';
import {
  PLATFORM_HTTP_CLIENT,
  OBSERVABLE_SERVICE,
  type PlatformHttpClient,
  type ObservableService,
  RequestContextService,
  createTimer,
} from '@lark-apaas/nestjs-common';

import type { MiaodaUserInfo } from '../types';

const SDK_MODULE = 'authn';

/**
 * 平台 InnerAPI「按 ID 批量查人」路径。
 *
 * 服务端路由为 `/innerapi/v1/app/:appID/account/list_users`（另有
 * `/force_innerapi/...` 别名），`/innerapi` 前缀在 PlatformHttpClient 的
 * baseURL 里（env `FORCE_AUTHN_INNERAPI_DOMAIN`），故此处只写相对部分，
 * 与同包 `account/user/convert` 的写法一致。
 *
 * 刻意不做成 `AuthNPaasModuleOptions` 的可覆盖字段——module options 是公开
 * API，加了就删不掉，而这里一旦要改只需发一个补丁版。
 */
const LIST_USERS_PATH = (appId: string): string => `/v1/app/${appId}/account/list_users`;

/** 单次批量查询的 ID 上限，与 getBatchLarkUserIds / getBatchMiaodaUserIds 一致 */
const MAX_BATCH_SIZE = 100;

/** RPC 层的 BaseResp。注意下游异常时 StatusCode 仍是 0，真正的错误在 KStatusCode 上 */
interface PlatformBaseResp {
  StatusCode?: number;
  StatusMessage?: string;
  KStatusCode?: string;
  KStatusMessage?: string;
}

/** 业务负载。网关可能把它整个包进 data，也可能摊在顶层，两种都要吃 */
interface ListUsersPayload {
  items?: MiaodaUserInfo[];
  status_code?: string | number;
  error_msg?: string;
  /** 部分服务端用 err_msg 而非 error_msg，两者都读，否则错误文案会被整段丢弃 */
  err_msg?: string;
  BaseResp?: PlatformBaseResp;
}

/** 平台响应体（宽松形态） */
interface ListUsersResponse extends ListUsersPayload {
  data?: ListUsersPayload;
}

function throwPlatformError(
  message: string,
  httpStatus: number,
  platformCode?: string | number,
  logId?: string,
): never {
  throw new HttpException(
    {
      statusCode: httpStatus,
      code: 'PLATFORM_API_ERROR',
      message,
      platformCode,
      'x-tt-logid': logId ?? '',
    },
    httpStatus,
  );
}

/**
 * 取平台错误文案，兼容 error_msg / err_msg 两种字段名、顶层与 data 两层位置。
 *
 * 与 nestjs-authzpaas 的 `pickErrorMessage` 同形：部分服务端用 err_msg 而非
 * error_msg，只读一个会把错误文案整段丢掉、退化成兜底文案，排查只剩 logid。
 */
function pickPlatformMessage(body: ListUsersResponse | undefined, fallback: string): string {
  return (
    body?.error_msg
    || body?.err_msg
    || body?.data?.error_msg
    || body?.data?.err_msg
    || fallback
  );
}

/** 只透出契约内的三个字段，防止平台将来多返回字段时被动扩大对外契约 */
function pickUserInfo(raw: MiaodaUserInfo): MiaodaUserInfo {
  return {
    miaodaUserID: String(raw.miaodaUserID),
    name: raw.name,
    avatar: raw.avatar,
  };
}

/**
 * 妙搭通讯录能力（`AuthNPaasService.listUsersByIds`，服务端 / OpenAPI 态可用）
 *
 * 与本类其他方法的区别：查人**不依赖调用者身份**，只用应用凭据（AK/SK 自签
 * JWT + x-api-key）+ 上下文里的 `appId`。因此在 OpenAPI 请求（没有 cookie、
 * 网关注入不了登录态、`RequestContext.userId` 为空）下同样可用。
 *
 * 服务端**没有**搜人 / 搜部门 / 搜群能力——搜索结果取决于「谁在搜」，
 * OpenAPI 态拿不到调用者视角。需要搜索请改走飞书开放平台通讯录 API。
 */

/**
 * 妙搭用户ID → 飞书用户ID 的转换类型
 */
const CONVERT_TYPE_FORCE_TO_FEISHU = 30;

/**
 * 飞书用户ID → 妙搭用户ID 的转换类型
 */
const CONVERT_TYPE_FEISHU_TO_FORCE = 31;

/**
 * ID 转换结果项
 */
export interface UserIDMappingItem {
  sourceUserID: string;
  targetUserID: string;
}

/**
 * 批量转换响应
 */
interface BatchConvertResponse {
  data?: {
    items?: UserIDMappingItem[];
  };
  status_code?: string;
  status_message?: string;
  error_msg?: string;
}

@Injectable()
export class AuthNPaasService {
  private readonly logger = new Logger(AuthNPaasService.name);

  constructor(
    @Inject(PLATFORM_HTTP_CLIENT) private readonly httpClient: PlatformHttpClient,
    @Inject(OBSERVABLE_SERVICE) private readonly obs: ObservableService,
    private readonly requestContext: RequestContextService,
  ) {}

  /**
   * 获取当前登录用户的飞书 user_id
   *
   * 从请求上下文获取当前妙搭 userId，调用转换接口获取对应的飞书 user_id
   *
   * @returns 飞书 user_id，如果当前用户未登录或转换失败返回 null
   */
  async getCurrentUserLarkUserId(): Promise<string | null> {
    const userId = this.requestContext.get('userId');
    if (!userId) {
      this.logger.warn('getCurrentUserLarkUserId: no userId in request context');
      return null;
    }

    const items = await this.batchConvertUserIds('getCurrentUserLarkUserId', [userId], CONVERT_TYPE_FORCE_TO_FEISHU);
    if (!items || items.length === 0) {
      return null;
    }

    return items[0].targetUserID || null;
  }

  /**
   * 批量将妙搭 userId 转换为飞书 user_id
   *
   * @param userIds - 妙搭用户 ID 列表，最多 100 个
   * @returns 飞书 user_id 数组，顺序与输入对应，转换失败的项为 null
   */
  async getBatchLarkUserIds(userIds: string[]): Promise<(string | null)[]> {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    if (userIds.length > 100) {
      throw new Error('userIds length must not exceed 100');
    }

    const items = await this.batchConvertUserIds('getBatchLarkUserIds', userIds, CONVERT_TYPE_FORCE_TO_FEISHU);
    const mapping = new Map(items.map(item => [item.sourceUserID, item.targetUserID]));
    return userIds.map(id => mapping.get(id) ?? null);
  }

  /**
   * 批量将飞书 user_id(employee_id)转换为妙搭 userId
   *
   * @param employeeIds - 飞书 user_id(employee_id)列表,最多 100 个
   * @returns 妙搭 userId 数组,顺序与输入对应,转换失败的项为 null
   */
  async getBatchMiaodaUserIds(employeeIds: string[]): Promise<(string | null)[]> {
    if (!employeeIds || employeeIds.length === 0) {
      return [];
    }

    if (employeeIds.length > 100) {
      throw new Error('employeeIds length must not exceed 100');
    }

    const items = await this.batchConvertUserIds('getBatchMiaodaUserIds', employeeIds, CONVERT_TYPE_FEISHU_TO_FORCE);
    const mapping = new Map(items.map(item => [item.sourceUserID, item.targetUserID]));
    return employeeIds.map(id => mapping.get(id) ?? null);
  }

  /**
   * 调用批量转换接口（带可观测性）
   */
  private async batchConvertUserIds(method: string, userIds: string[], convertType: number): Promise<UserIDMappingItem[]> {
    const appId = this.requestContext.get('appId');
    if (!appId) {
      this.logger.warn(`${method}: no appId in request context`);
      return [];
    }

    const url = `/v1/app/${appId}/account/user/convert`;
    const body = {
      convertType,
      userIDList: userIds,
    };

    const spanName = `${SDK_MODULE}.${method}`;
    const spanAttrs: Record<string, unknown> = {
      module: SDK_MODULE,
      method,
      source_type: 'platform',
      paas_attributes_module: SDK_MODULE,
      http_method: 'POST',
      path: url,
    };
    const logAttrs: Record<string, unknown> = {
      source_type: 'platform',
      paas_attributes_module: SDK_MODULE,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.obs.trace(spanName, async (span: any) => {
      span.setAttributes(spanAttrs);
      const elapsed = createTimer();

      try {
        this.logger.log(`[batchConvertUserIds] request: ${url} ${JSON.stringify(body)}`);
        const response = await this.httpClient.post(url, body);
        const logId = response.headers?.get?.('x-tt-logid') ?? '';
        const raw = await response.text();
        const durationMs = elapsed();

        this.logger.log(`[batchConvertUserIds] response: status=${response.status} ok=${response.ok} logId=${logId} raw=${raw}`);

        if (!response.ok) {
          span.setAttributes({ duration_ms: durationMs, success: false, error: `HTTP ${response.status}`, 'x-tt-logid': logId });
          this.logger.warn(JSON.stringify({
            method, path: url, duration_ms: durationMs, success: false,
            error: `HTTP ${response.status} ${response.statusText}`,
            'x-tt-logid': logId, params: { convertType, count: userIds.length },
          }), logAttrs);
          return [];
        }

        const result: BatchConvertResponse = raw ? JSON.parse(raw) : {};

        if (String(result.status_code) !== '0' && result.status_code !== undefined) {
          const errMsg = `[${result.status_code}] ${result.status_message || result.error_msg}`;
          span.setAttributes({ duration_ms: durationMs, success: false, error: errMsg, 'x-tt-logid': logId });
          this.logger.error(JSON.stringify({
            method, path: url, duration_ms: durationMs, success: false,
            error: errMsg, 'x-tt-logid': logId,
            params: { convertType, count: userIds.length },
          }), logAttrs);
          return [];
        }

        const items = (result.data?.items ?? []).filter(
          (item): item is UserIDMappingItem => item != null && item.sourceUserID != null,
        );

        span.setAttributes({ duration_ms: durationMs, success: true, 'x-tt-logid': logId, result_count: items.length });
        this.logger.log(JSON.stringify({
          method, path: url, duration_ms: durationMs, success: true,
          'x-tt-logid': logId,
          params: { convertType, count: userIds.length },
          result_count: items.length,
          response: raw.length > 1024 ? raw.slice(0, 1024) + '...' : raw,
        }), logAttrs);

        return items;
      } catch (error: unknown) {
        const durationMs = elapsed();
        const errMsg = error instanceof Error ? error.message : String(error);

        const err = error as Record<string, unknown> | undefined;
        const logId =
          (err?.headers as { get?: (k: string) => string })?.get?.('x-tt-logid')
          ?? (err?.response as { headers?: Record<string, string> })?.headers?.['x-tt-logid']
          ?? err?.logId
          ?? '';
        this.logger.error(`[batchConvertUserIds] CATCH: ${errMsg} logId=${logId}`);

        span.setAttributes({ duration_ms: durationMs, success: false, error: errMsg });
        this.logger.warn(JSON.stringify({
          method, path: url, duration_ms: durationMs, success: false,
          error: errMsg,
          params: { convertType, count: userIds.length },
        }), logAttrs);
        return [];
      }
    });
  }

  /**
   * 按妙搭 userID 批量查用户信息（姓名 + 头像）
   *
   * @param userIds - 妙搭用户 ID 列表，最多 100 个
   * @returns 与入参**等长且同序**的数组；未命中的位置为 `null`
   *
   * @throws {Error} 入参超过 100 个，或请求上下文中缺少 appId
   * @throws {HttpException} 平台调用失败（502），错误信息含平台 code 与 x-tt-logid
   *
   * @example
   * ```ts
   * const [alice, bob] = await contacts.listUsersByIds([aliceId, bobId]);
   * const displayName = alice?.name?.zh_cn ?? alice?.name?.en_us ?? '';
   * const avatarUrl = alice?.avatar?.image?.large;
   * ```
   */
  async listUsersByIds(userIds: string[]): Promise<(MiaodaUserInfo | null)[]> {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    if (userIds.length > MAX_BATCH_SIZE) {
      throw new Error(`userIds length must not exceed ${MAX_BATCH_SIZE}`);
    }

    const appId = this.requestContext.get('appId');
    if (!appId) {
      // 刻意不静默返回空——OpenAPI 态查不到人和「上下文没配好」是两回事
      throw new Error('listUsersByIds: no appId in request context');
    }

    // 去重 + 去空串；请求体只带有效 ID，返回仍按原始入参对齐
    const queryIds = [...new Set(userIds.filter(id => !!id))];
    if (queryIds.length === 0) {
      return userIds.map(() => null);
    }

    const url = LIST_USERS_PATH(appId);
    const body = { userIDList: queryIds };

    const items = await this.requestListUsers(url, body);
    const found = new Map<string, MiaodaUserInfo>(
      items
        .filter(item => item != null && item.miaodaUserID != null)
        .map(item => [String(item.miaodaUserID), pickUserInfo(item)]),
    );

    // 每个位置给一份独立拷贝：入参含重复 ID 时若共享引用，调用方改一处会连带改
    // 另一处。必须是结构化拷贝而非 `{ ...hit }`——浅拷贝只隔离顶层，name / avatar
    // 仍是同一份引用，改 `first.avatar.image.large` 照样连带改 second。
    return userIds.map(id => {
      const hit = id ? found.get(id) : undefined;
      return hit ? structuredClone(hit) : null;
    });
  }

  /** 发起平台调用并解析出 items（带可观测性） */
  private async requestListUsers(
    url: string,
    body: { userIDList: string[] },
  ): Promise<MiaodaUserInfo[]> {
    const method = 'listUsersByIds';
    const spanAttrs: Record<string, unknown> = {
      module: SDK_MODULE,
      method,
      source_type: 'platform',
      paas_attributes_module: SDK_MODULE,
      http_method: 'POST',
      path: url,
    };
    const logAttrs: Record<string, unknown> = {
      source_type: 'platform',
      paas_attributes_module: SDK_MODULE,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.obs.trace(`${SDK_MODULE}.${method}`, async (span: any) => {
      span.setAttributes(spanAttrs);
      const elapsed = createTimer();

      try {
        const response = await this.httpClient.post(url, body);
        const logId = response.headers?.get?.('x-tt-logid') ?? '';
        const raw = await response.text();
        const items = parseListUsersResponse(response, raw, logId);
        const durationMs = elapsed();

        span.setAttributes({
          duration_ms: durationMs,
          success: true,
          'x-tt-logid': logId,
          result_count: items.length,
        });
        this.logger.log(
          JSON.stringify({
            method,
            path: url,
            duration_ms: durationMs,
            success: true,
            'x-tt-logid': logId,
            params: { count: body.userIDList.length },
            result_count: items.length,
          }),
          logAttrs,
        );

        return items;
      } catch (error: unknown) {
        const durationMs = elapsed();
        const errMsg = error instanceof Error ? error.message : String(error);
        const logId =
          error instanceof HttpException
            ? ((error.getResponse() as Record<string, unknown>)?.['x-tt-logid'] as string) ?? ''
            : '';

        span.setAttributes({
          duration_ms: durationMs,
          success: false,
          error: errMsg,
          ...(logId ? { 'x-tt-logid': logId } : {}),
        });
        this.logger.warn(
          JSON.stringify({
            method,
            path: url,
            duration_ms: durationMs,
            success: false,
            error: errMsg,
            'x-tt-logid': logId,
            params: { count: body.userIDList.length },
          }),
          logAttrs,
        );

        throw error;
      }
    });
  }
}

/**
 * 解析平台响应。
 *
 * 三个判据并联，任一命中即视为失败：
 * 1. HTTP 非 2xx —— 传输 / 网关层
 * 2. `status_code` 非 '0' —— HTTP InnerAPI 业务层
 * 3. `BaseResp.KStatusCode` 非空 —— RPC 层。**关键**：平台在下游异常时
 *    仍会返回 `StatusCode: 0`，只判 status_code 会把 `tenant not found`
 *    静默降级成「查无此人」。
 *
 * 判据 2/3 与 items 一样，顶层和 `data` 下都查——网关是否包一层未定，
 * 只查一处会让被包裹的错误漏网，等于把要消灭的失效模式又放进来一遍。
 */
function parseListUsersResponse(
  response: { ok: boolean; status: number },
  raw: string,
  logId: string,
): MiaodaUserInfo[] {
  if (!response.ok) {
    let err: ListUsersResponse | undefined;
    try {
      err = JSON.parse(raw);
    } catch {
      /* 错误体不是 JSON 时忽略，用兜底文案 */
    }
    const message = pickPlatformMessage(err, `平台 API 请求失败 (${response.status})`);
    const httpStatus =
      response.status >= 500 ? HttpStatus.BAD_GATEWAY : (response.status as number);
    throwPlatformError(message, httpStatus, err?.status_code, logId);
  }

  if (!raw) {
    return [];
  }

  let json: ListUsersResponse;
  try {
    json = JSON.parse(raw);
  } catch {
    throwPlatformError('平台 API 响应不是合法 JSON', HttpStatus.BAD_GATEWAY, undefined, logId);
  }

  // body 是字面量 "null" 时 JSON.parse 得到 null，后续取字段会崩
  if (json == null) {
    return [];
  }

  // 错误标记与 items 一样，顶层和 data 下都要查——只查一处会让被包裹的
  // 下游异常漏网，正是本服务要消灭的那种「静默降级成查无此人」
  const statusCode = json.status_code ?? json.data?.status_code;
  if (statusCode != null && String(statusCode) !== '0') {
    throwPlatformError(
      pickPlatformMessage(json, `平台业务错误 (${statusCode})`),
      HttpStatus.BAD_GATEWAY,
      statusCode,
      logId,
    );
  }

  const baseResp = json.BaseResp ?? json.data?.BaseResp;
  const kStatusCode = baseResp?.KStatusCode;
  if (kStatusCode) {
    throwPlatformError(
      baseResp?.KStatusMessage || baseResp?.StatusMessage || `平台业务错误 (${kStatusCode})`,
      HttpStatus.BAD_GATEWAY,
      kStatusCode,
      logId,
    );
  }

  // 跨租户 / 全部未命中 / 空入参时平台不返回 items 键（不是空数组）
  return json.data?.items ?? json.items ?? [];
}
